import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Helper functions to manage cookies
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return '';
};

function PhotoUpload() {
  const { authTokens } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [userInfo, setUserInfo] = useState('');
  const [error, setError] = useState('');

  // API Endpoint URL
  const API_ENDPOINT = 'https://chpfmg4d6d.execute-api.us-east-1.amazonaws.com/v1/process';

  useEffect(() => {
    // Prevent default behavior for dragover and drop events on the window
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);

    // Retrieve user info from the cookie (if it exists)
    const savedUserInfo = getCookie('user_info');
    if (savedUserInfo) {
      setUserInfo(savedUserInfo);
    }

    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  const isValidFileType = (file) => {
    const acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    return acceptedTypes.includes(file.type);
  };

  const handleImageChange = (e) => {
    let file;
    if (e.dataTransfer) {
      // Handle dropped files
      file = e.dataTransfer.files[0];
    } else {
      // Handle selected files
      file = e.target.files[0];
    }

    if (file && isValidFileType(file)) {
      setImage(file);
      setError('');
      console.log('Selected image:', file);

      // Generate image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImage(null);
      setImagePreview(null);
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
    }
  };

  // Drag and Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    // Process the dropped files
    handleImageChange(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!image) {
      setError('Please select an image');
      console.log('No image selected.');
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append('image', image, image.name);
    formData.append('description', description);
    formData.append('user_info', userInfo);

    // Save the user info to a cookie
    setCookie('user_info', userInfo, 7); // Save the cookie for 7 days

    console.log('Submitting form data:', {
      image: image.name,
      description,
      user_info: userInfo,
    });

    try {
      const response = await axios.post(API_ENDPOINT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Received response:', response.data);
      navigate('/results', { state: { resultData: response.data } });
    } catch (error) {
      console.error('Error processing image:', error);
      if (error.response && error.response.data && error.response.data.message === "Endpoint request timed out") {
        setError('The system is currently busy. Please try again later.');
      } else {
        setError('Error processing image. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Share feature using the Web Share API
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Check out this awesome food analysis app!',
        text: 'I just used this app to analyze my food. You should try it out!',
        url: window.location.href, // Share the current URL
      })
          .then(() => console.log('Thanks for sharing!'))
          .catch((error) => console.log('Error sharing:', error));
    } else {
      alert('Your browser does not support the Web Share API.');
    }
  };

  return (
      <div className={`photo-upload-container ${theme}`}>
        <div className="form-container">
          <h2 className="form-heading">Upload a Food Photo</h2>
          <form onSubmit={handleSubmit} className="upload-form">
            <div
                className={`form-group custom-file-upload ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
              <label htmlFor="imageUpload">
                {imagePreview ? (
                    <img src={imagePreview} alt="Selected" className="image-preview" />
                ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>Drag & Drop or Click to Choose Image (JPEG, PNG, GIF, or WebP)</span>
                    </>
                )}
              </label>
              <input
                  type="file"
                  id="imageUpload"
                  onChange={handleImageChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Optional Description
              </label>
              <input
                  type="text"
                  id="description"
                  className="form-control"
                  placeholder="Give more details for more precise results"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="form-group">
              <div className="button-wrapper">
                <button
                    type="button"
                    className="btn btn-link toggle-info-btn"
                    onClick={() => setShowUserInfo(!showUserInfo)}
                >
                  {showUserInfo ? 'Hide' : 'Show'} Personal Info
                </button>
              </div>
              {showUserInfo && (
                  <textarea
                      id="userInfo"
                      className="form-control"
                      placeholder="Enter detailed info for more personalized results (age, weight, diet, dislikes, meds, etc). Nothing's ever saved 👍"
                      value={userInfo}
                      onChange={(e) => setUserInfo(e.target.value)}
                      rows="4"
                  />
              )}
            </div>
            <button
                type="submit"
                className="btn btn-primary submit-button"
                disabled={isLoading}
            >
              {isLoading ? (
                  <>
                    <span className="spinner"></span> Processing...
                  </>
              ) : (
                  'Analyze My Food'
              )}
            </button>
          </form>

          {/* Share Button at the bottom */}
          <div className="share-container" style={{ textAlign: 'center', marginTop: '20px' }}>
            <button onClick={handleShare} className="btn btn-outline-primary">
              Share this App
            </button>
          </div>
        </div>
      </div>
  );
}

export default PhotoUpload;